/**
 * @file SkeletonEvidenceCard.tsx
 *
 * @description A loading placeholder component that mimics the appearance of an evidence card.
 * This component is displayed during data fetching operations to improve perceived
 * performance and provide visual feedback to law enforcement users.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import React from "react";
import { Card, CardContent, Box, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function SkeletonEvidenceCard
 *
 * @description A placeholder component that mimics the layout and structure of an evidence card
 * using Skeleton animations. This component should be shown while the actual evidence
 * data is loading to provide visual feedback to police officers using the application.
 *
 * The component follows the same dimensions and layout as the actual EvidenceCard
 * to prevent layout shifts when the real content loads.
 *
 * @returns {JSX.Element} A skeleton loading representation of an evidence card
 */
const SkeletonEvidenceCard: React.FC = () => {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Card
        sx={{
          height: { xs: "auto", sm: "310px" },
          width: "100%",
          minWidth: "200px",
          maxWidth: "300px",
          margin: "auto",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          borderColor: "divider",
          borderWidth: 1,
          borderStyle: "solid",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Evidence filename placeholder */}
          <Skeleton variant="text" width="90%" height={40} animation="wave" />

          {/* Divider */}
          <Box sx={{ borderBottom: 2, borderColor: 'divider', borderStyle: 'solid', mb: 1 }} />

          {/* Date */}
          <Skeleton
            variant="text"
            width="100%"
            height={24}
            animation="wave"
            sx={{ mb: 1 }}
          />

          {/* Duration */}
          <Skeleton variant="text" width="70%" height={24} animation="wave" />

          {/* Device */}
          <Skeleton variant="text" width="80%" height={24} animation="wave" />

          {/* Status */}
          <Skeleton variant="text" width="60%" height={24} animation="wave" />
        </CardContent>

        {/* Button */}
        <Box sx={{ p: 2, pt: 0, mt: "auto" }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={40}
            animation="wave"
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      </Card>
    </Grid>
  );
};

export default SkeletonEvidenceCard;
